import React, {useEffect, useState} from "react";

import {AddToastFunction} from "../../../utils/types/toast";
import {
    Accordion,
    AccordionPanel, Button, ButtonGroup,
    Card,
    DataTable, DataTableCell,
    DataTableColumn, DataTableRowActions, Dropdown, Icon, PageHeader, PageHeaderControl, Tooltip, Spinner
} from "@salesforce/design-system-react";
import CreateReasonModal from "../../../modals/pcsk_admin/deployments/operations/CreateReason";
import EditReasonModal from "../../../modals/pcsk_admin/deployments/operations/EditReason";
import ConfirmationModal from "../../../modals/pcsk_admin/deployments/operations/ConfirmationModal";
import {createNewReason, deleteReason, fetchReasons, updateReason} from "../../../services/reasons";
import {CONSTANT_JIT_OPERATION} from "../../../constants/pcsk_admin/jitOperations";
import {ADMIN_SERVICE_STATUS} from "../../../services/environment/environment";

type PCSKReasonUpdatePageProps = {
    addToast: AddToastFunction;
    navigationBar: React.ReactNode;
};

type Reason = {
    id: string,
    label: string,
    value: string,
    description: string,
    url: string,
    note: string,
    is_active: boolean,
    minor_reasons: Reason[],
    sort_order?: number,
    show_recommended_actions: boolean,
}

export type CustomRecommendedActionCellProps = {
    children?: React.ReactNode;
};
const CustomRecommendedActionTableCell: React.FC<CustomRecommendedActionCellProps> = ({ children, ...props }) => {
    return (
        <div className="slds-cell-wrap slds-m-around--x-small">
            {children === true && "Yes"}
            {children === false && "No"}
        </div>
    );
};
CustomRecommendedActionTableCell.displayName = DataTableCell.displayName;

const PCSKReasonUpdatePage: React.FC<PCSKReasonUpdatePageProps> = ({navigationBar, addToast}) => {

    const [reasons, setReasons] = useState<Reason[]>([]);
    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
    const [showCreateReasonModal, setShowCreateReasonModal] = useState<boolean>(false);
    const [showEditReasonModal, setShowEditReasonModal] = useState<boolean>(false);
    const [editableReasonDetails, setEditableReasonDetails] = useState<any>(null);
    const [selectedMajorReason, setSelectedMajorReason] = useState<any>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [reasonToBeDelete, setReasonToBeDelete] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    const togglePanel = (id: string) => {
        setExpandedPanels((prev) =>
            prev.includes(id) ? [] : [id]
        );
    };

    const toggleCreateReasonModal = () => {
        setShowCreateReasonModal(false);
    }

    const toggleEditReasonModal = () => {
        setShowEditReasonModal(false);
        setEditableReasonDetails(null);
    }

    const handleCreateMajorReason = () => {
        setSelectedMajorReason(null);
        setShowCreateReasonModal(true);
    }

    const handleCreateMinorReason = (data: any) => {
        setSelectedMajorReason(data);
        setShowCreateReasonModal(true);
    }

    const editMajorReasonModal = (item: any) => {
        setShowEditReasonModal(true);
        setSelectedMajorReason(null);
        setEditableReasonDetails(item);
    }

    const handleRowAction = (item: any, action: any, parentMajorReason: any) => {
        if(action.id === "edit_minor_reason") {
            setEditableReasonDetails(item);
            setSelectedMajorReason(parentMajorReason);
            setShowEditReasonModal(true);
        }
        if(action.id === "delete_minor_reason") {
            setReasonToBeDelete(item);
            setConfirmDialogOpen(true);
        }
    }

    const confirmDeleteAction = () => {
        deleteReason(reasonToBeDelete.id).then(response => {
            loadReasons();
            addToast(CONSTANT_JIT_OPERATION.REASON_DELETION_REQ_SUBMIT, `Reason: ${reasonToBeDelete.value}`, "success");
        }).catch(err => {
            addToast(CONSTANT_JIT_OPERATION.REASON_SUBMIT_ERROR, err.message, "error");
            console.log(err);
        }).finally(() => {
            setReasonToBeDelete(null);
        });
        setConfirmDialogOpen(false);
    }

    const deleteMajorReason = (item: any) => {
        setReasonToBeDelete(item);
        setConfirmDialogOpen(true);
    }

    const onSubmitCreateReason = (data: any) => {
        setIsLoading(true);
        createNewReason(data).then((response: any) => {
            addToast(CONSTANT_JIT_OPERATION.REASON_CREATION_REQ_SUBMIT, "", "success");
            loadReasons();
        }).catch(err => {
            addToast(CONSTANT_JIT_OPERATION.REASON_SUBMIT_ERROR, err.message, "error");
            console.log(err);
        }).finally(() => {
            setReasonToBeDelete(null);
            setIsLoading(false);
        })
    }

    const onSubmitUpdateReason = (item: any) => {
        setIsLoading(true);
        updateReason(item).then(response => {
            addToast(CONSTANT_JIT_OPERATION.REASON_UPDATE_REQ_SUBMIT, "", "success");
            loadReasons();
            setEditableReasonDetails(null);
            setSelectedMajorReason(null);
        }).catch(err => {
            addToast(CONSTANT_JIT_OPERATION.REASON_SUBMIT_ERROR, err.message, "error");
            console.log(err);
        }).finally(() => {
            setReasonToBeDelete(null);
            setIsLoading(false);
        })

    }

    const loadReasons = () => {
        setIsLoading(true);
        fetchReasons()
            .then((data) => {
                const transformedData = data.map((reason: Reason) => ({
                    id: reason.id,
                    label: reason.label,
                    value: reason.value,
                    description: reason.description,
                    url: reason.url,
                    note: reason.note,
                    is_active: reason.is_active,
                    minor_reasons: reason.minor_reasons.sort((a: any, b: any) => {
                        // First sort by sort_order if available
                        if (a.sort_order !== undefined && b.sort_order !== undefined) {
                            return a.sort_order - b.sort_order;
                        }
                        // Fallback to alphabetical sorting
                        return a.label.localeCompare(b.label);
                    }),
                    sort_order: reason.sort_order || 0,
                    show_recommended_actions: reason.show_recommended_actions,
                })).sort((a:any, b: any) => {
                    // First sort by sort_order if available
                    if (a.sort_order !== undefined && b.sort_order !== undefined) {
                        return a.sort_order - b.sort_order;
                    }
                    // Fallback to alphabetical sorting
                    return a.label.localeCompare(b.label);
                });
                setReasons(transformedData);
            })
            .catch(() => setReasons([]))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadReasons(); // Trigger on page load
    }, []);

    const actions = () => (
        <PageHeaderControl>
            <ButtonGroup id="button-group-page-header-actions">
                <Button
                    label={CONSTANT_JIT_OPERATION.NEW_MAJOR_REASON}
                    variant="brand"
                    onClick={handleCreateMajorReason}
                />
            </ButtonGroup>
        </PageHeaderControl>
    );

    // Check if the feature is enabled
    if (!ADMIN_SERVICE_STATUS.isReasonAPIEnabled()) {
        return (
            <div>
                {navigationBar}
                <div style={{minHeight: "80vh"}} className="slds-box">
                    <div className="slds-text-align_center slds-p-vertical_xx-large">
                        <Icon
                            assistiveText={{ label: 'Info' }}
                            category="utility"
                            name="info"
                            size="large"
                            className="slds-m-bottom_medium"
                        />
                        <h2 className="slds-text-heading_medium slds-m-bottom_small">
                            Feature Not Available
                        </h2>
                        <p className="slds-text-body_regular slds-text-color_weak">
                            The Reasons configuration feature is not currently enabled for this environment.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return <div>
        {navigationBar}
        <div style={{minHeight: "80vh"}} className="slds-box">
            <PageHeader
                onRenderActions={actions}
                icon={
                    <Icon
                        assistiveText={{ label: 'User' }}
                        category="standard"
                        name="lead"
                    />
                }
                info={CONSTANT_JIT_OPERATION.DISPLAY_HEADER_MSG}
                label={CONSTANT_JIT_OPERATION.OPERATION}
                title={CONSTANT_JIT_OPERATION.REASON_CONFIGURATION_HEADER}
                truncate
                variant="object-home"
            />
            <Card
                hasNoHeader={true}
                >
                <div>
                    <Accordion>
                        {isLoading ? (
                            <div className="slds-text-align_center slds-p-vertical_xx-large">
                                <Spinner size="medium" assistiveText={{label: "Loading reasons"}} />
                                <p className="slds-text-body_regular slds-text-color_weak">Loading reasons...</p>
                            </div>
                        ) : reasons.length === 0 ? (
                            <div className="slds-text-align_center slds-p-vertical_xx-large">
                                <Icon
                                    assistiveText={{ label: 'Info' }}
                                    category="utility"
                                    name="info"
                                    size="large"
                                    className="slds-m-bottom_medium"
                                />
                                <h2 className="slds-text-heading_medium slds-m-bottom_small">
                                    No Reasons Found
                                </h2>
                                <p className="slds-text-body_regular slds-text-color_weak">
                                    No reasons have been configured yet. Click the "New Major Reason" button to add one.
                                </p>
                            </div>
                        ) : (
                            reasons.map((data, index) => (
                                <AccordionPanel
                                    key={index}
                                    id={`accordion-panel-${index}`}
                                    expanded={expandedPanels.includes(String(index))}
                                    onTogglePanel={() => togglePanel(String(index))}
                                    title={data.label}
                                    summary={data.label}
                                    className="slds-m-bottom_small"
                                >
                                    <div className="slds-m-left--large">
                                        <div className="slds-m-bottom_small slds-text-body_regular slds-p-right--small">
                                            <div className="slds-p-vertical_xx-small slds-grid slds-grid_align-spread">
                                                <div>
                                                    <strong>Description:</strong> {data.description || '-'}
                                                </div>
                                                <ButtonGroup id="button-group-more-icon">
                                                    <Tooltip
                                                        content="Edit Major Reason"
                                                        align="top"
                                                    >
                                                        <Button
                                                            // label="Edit" variant="neutral"
                                                            iconCategory="utility"
                                                            iconName="edit"
                                                            iconVariant="border"
                                                            variant="icon"
                                                            onClick={() => editMajorReasonModal(data)}/>
                                                    </Tooltip>
                                                    <Tooltip
                                                        content="Delete Major Reason"
                                                        align="top"
                                                    >
                                                        <Button
                                                            // label="Delete" variant="destructive"
                                                            iconCategory="utility"
                                                            iconName="delete"
                                                            iconVariant="border"
                                                            variant="icon"
                                                            disabled={data.minor_reasons.length > 0}
                                                            onClick={() => deleteMajorReason(data)}
                                                        />
                                                    </Tooltip>
                                                </ButtonGroup>
                                            </div>
                                            <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
                                                <strong>Sort Order:</strong> {data.sort_order}
                                            </div>
                                        </div>
                                        <div>
                                            <div
                                                className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-top_small slds-p-bottom_x-small slds-p-right_x-small">
                                                <div className="slds-text-heading_small">
                                                    {CONSTANT_JIT_OPERATION.MINOR_REASONS}
                                                </div>
                                                <div className="slds-p-right--x-small">
                                                    <Tooltip
                                                        content={CONSTANT_JIT_OPERATION.CREATE_MINOR_REASON}
                                                        align="top"
                                                    >
                                                        <Button assistiveText={{icon: CONSTANT_JIT_OPERATION.CREATE_MINOR_REASON}}
                                                                iconCategory="utility"
                                                                iconName="add"
                                                                iconVariant="border"
                                                                variant="icon"
                                                                onClick={() => handleCreateMinorReason(data)}/>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            <DataTable
                                                items={data.minor_reasons}
                                                id={`minor-reasons-table-${index}`}
                                                fixedLayout
                                                bordered
                                                showRowHover
                                            >
                                                <DataTableColumn label={CONSTANT_JIT_OPERATION.LABEL} property="label"/>
                                                <DataTableColumn label={CONSTANT_JIT_OPERATION.DESCRIPTION} property="description"/>
                                                <DataTableColumn label={CONSTANT_JIT_OPERATION.URL} property="url"/>
                                                <DataTableColumn label={CONSTANT_JIT_OPERATION.NOTE} property="note"/>
                                                <DataTableColumn label={CONSTANT_JIT_OPERATION.SORT_ORDER} property="sort_order" width="7rem"/>
                                                <DataTableColumn
                                                    label={CONSTANT_JIT_OPERATION.SHOW_RECOMMENDED_ACTIONS}
                                                    property="show_recommended_actions"
                                                    width="14rem"
                                                >
                                                    <CustomRecommendedActionTableCell/>
                                                </DataTableColumn>
                                                <DataTableRowActions options={[
                                                    {
                                                        id: 'edit_minor_reason',
                                                        label: CONSTANT_JIT_OPERATION.EDIT,
                                                        value: 'edit_minor_reason',
                                                    },
                                                    {
                                                        id: 'delete_minor_reason',
                                                        label: CONSTANT_JIT_OPERATION.DELETE,
                                                        value: 'delete_minor_reason',
                                                    }
                                                ]}
                                                                     onAction={(item: any, action: any) => handleRowAction(item, action, data)}
                                                                     dropdown={<Dropdown length="7"
                                                                                         menuPosition="overflowBoundaryElement"/>}/>
                                            </DataTable>
                                        </div>
                                    </div>
                                </AccordionPanel>
                            ))
                        )}
                    </Accordion>
                </div>
            </Card>
            <CreateReasonModal
                isOpen={showCreateReasonModal}
                toggleModal={(isOpen) => setShowCreateReasonModal(isOpen)}
                onCancel={toggleCreateReasonModal}
                onCreate={onSubmitCreateReason}
                majorReason={selectedMajorReason}
            />
            <EditReasonModal
                isOpen={showEditReasonModal}
                toggleModal={(isOpen) => setShowEditReasonModal(isOpen)}
                onCancel={toggleEditReasonModal}
                reasonData={editableReasonDetails}
                onUpdate={onSubmitUpdateReason}
                majorReason={selectedMajorReason}
            />
            <ConfirmationModal
                isOpen={confirmDialogOpen}
                toggleModal={(isOpen) => setConfirmDialogOpen(isOpen)}
                onConfirm={confirmDeleteAction}
                actionType="Delete"
            />
        </div>
    </div>
};


export default PCSKReasonUpdatePage;